import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import useQueryParams from 'Hooks/useQueryParams';
import { StudyPlanEntity } from 'Models/Entities';
import alertToast from 'Util/ToastifyUtils';

import Wizard from 'Views/Components/Wizard/Wizard';
import {
	WizardContextProvider, useWizardContext, StepValidator, WizardMode,
} from 'Views/Components/Wizard/WizardContext';
import StructureSelectionPage from 'Views/Pages/Frontend/StudyPlanWizard/ProgramStructure/StructureSelectionPage';
import CourseSelectionPage from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/CourseSelectionPage';
import RequirementFulfilmentCheckPage
	from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/RequirementFulfilmentCheckPage';

import { savePlanSelections } from 'Queries/REST/StudyPlan/StudyPlanRequests';

import { StudyPlan } from 'Views/Pages/index';
import { IStepLabel } from 'Views/Components/Steps/Steps';
import { useMounted } from 'Hooks/useMounted';
import useAsync from 'Hooks/useAsync';

import type { Subset } from 'Util/TypeHelpers';
import { AbortSignals } from 'Hooks/useAbortSignals';
import { capitalize } from 'lodash';

type fetchPlanRequestState = 'loading' | 'error' | 'done';

export interface IStudyPlanWizardData {
	fetchPlanRequestState: fetchPlanRequestState,
	studyPlan: StudyPlanEntity | undefined,
}

const StudyPlanWizard = () => {
	const history = useHistory();
	const queryParams = useQueryParams();
	const mounted = useMounted();

	const {
		step,
		setData,
	} = useWizardContext<IStudyPlanWizardData>();

	const currentPage = useMemo(() => {
		switch (step) {
			case 1:
				return <StructureSelectionPage />;
			case 2:
				return <CourseSelectionPage />;
			case 3:
				return <RequirementFulfilmentCheckPage />;
			case 4:
				return <StudyPlan />;
			default:
				return <></>;
		}
	}, [step]);

	// extract study plan id from query parameters
	const studyPlanId = useMemo(() => {
		if (!queryParams.has('id')) {
			// alertToast('Study Plan is not specified', 'error'); // decided to redirect silently instead
			history.replace('/');
		}

		return queryParams.get('id');
	}, [history, queryParams]);

	// Initialize study plan data
	useAsync(
		async () => {
			if (!studyPlanId) {
				return;
			}

			setData(prevState => ({
				...prevState,
				fetchPlanRequestState: 'loading',
			}));

			try {
				const saveResponse = await savePlanSelections(undefined, studyPlanId);

				if (!mounted.current || !saveResponse) {
					return;
				}

				const { data: { studyPlan } } = saveResponse;

				if (mounted.current) {
					setData(prevState => ({
						...prevState,
						studyPlan: new StudyPlanEntity(studyPlan),
						fetchPlanRequestState: 'done',
					}));
				}
			} catch (err) {
				if (!mounted.current) {
					return;
				}
				if (err instanceof Error) {
					console.error(err);
					alertToast('Fail to retrieve courses', 'error');
				}
				setData(prevState => ({
					...prevState,
					fetchPlanRequestState: 'error',
				}));
			}
		},
		[mounted, setData, studyPlanId],
	);

	return (
		<Wizard className="create-study-plan-wizard">
			{currentPage}
		</Wizard>
	);
};

export const StudyPlanWizardContext = (props: any) => {
	const initialData: IStudyPlanWizardData = useMemo(() => ({
		fetchPlanRequestState: 'loading',
		studyPlan: undefined,
		compulsoryCourseOfferingIds: undefined,
	}), []);

	const stepLabels: IStepLabel[] = [
		{
			label: `${capitalize(window.lexicon.program)} ${window.lexicon.structure.toLowerCase()}`,
		},
		{
			label: `${capitalize(window.lexicon.course)} selection`,
		},
		{
			label: `${capitalize(window.lexicon.requirementsFulfilmentCheck)}`,
		},
		{
			label: `${capitalize(window.lexicon.studyPlan)}`,
		},
	];

	const onChangeAndSave = useCallback(async (
		abortSignals: AbortSignals,
		data: IStudyPlanWizardData,
	): Promise<Subset<IStudyPlanWizardData> | void> => {
		if (data.studyPlan) {
			// this includes selected and required requirements
			const selectedRequirements = data.studyPlan.studyPlanRequirements
				.map(requirementJoin => requirementJoin.studyPlanRequirementId);

			try {
				const { data: responseData } = await savePlanSelections(
					abortSignals.axiosCancelToken,
					data.studyPlan.id,
					data.studyPlan.programStructureId,
					selectedRequirements,
					data.studyPlan.planSelections,
				);

				const updatedPlan = new StudyPlanEntity({
					...data.studyPlan, // preserve data in state not returned by save api
					planSelections: responseData.studyPlan.planSelections,
				});

				return {
					studyPlan: updatedPlan,
				};
			} catch (err) {
				console.error(err);
			}
		}
	}, []);

	const hasSufficientSelectedComponents = (studyPlan: StudyPlanEntity) => {
		const requiredComponentCount = studyPlan.programStructure.components
			.reduce((count: number, component) => {
				if (component.condition === 'AND') {
					// all requirement must be selected
					return count + component.requirements.length;
				}
				if (component.condition === 'OR') {
					// only 1 requirement should be selected
					return count + 1;
				}
				throw new Error('Reached unreachable point when validating selected Component requirements');
			}, 0);

		const actualSelectedComponentCount = studyPlan.studyPlanRequirements.length || 0;

		if (requiredComponentCount === undefined) {
			throw new Error('Could not validate number of requirements');
		}
		/**
		 * there should be 1 requirement assigned to a study plan for
		 * each component present in it's program structure
		 */
		return requiredComponentCount !== 0 && requiredComponentCount <= actualSelectedComponentCount;
	};

	const validators: Array<StepValidator<IStudyPlanWizardData>> = [
		// Program structure
		async data => {
			const isDependantDataLoading = data.fetchPlanRequestState === 'loading';

			const isProgramStructureSelected = !!data.studyPlan?.programStructureId;

			// count how many requirements should be selected based on component condition
			const sufficientComponentsAreSelected = (
				!!data.studyPlan
				&& isProgramStructureSelected
				&& hasSufficientSelectedComponents(data.studyPlan)
			);

			// data loading takes precence - we don't know the real validity
			// until we have data to validate
			const isStepValid = isDependantDataLoading
			|| (isProgramStructureSelected && sufficientComponentsAreSelected);

			/**
			 * this is so it remains valid until the data has
			 * loaded, in which it will either stay on the page
			 * or trigger a redirect
			 */
			return [
				isStepValid,
				{
					programStructureId: [
						isProgramStructureSelected
							? undefined
							: `A ${window.lexicon.structure.toLowerCase()} must be specified`,
					],
					component: [
						sufficientComponentsAreSelected
							? undefined
							: `A ${window.lexicon.component.toLowerCase()} must be selected`,
					],
				},
			];
		},
		// Course selection
		async () => [true, {}],
		// Requirement fulfilment check
		async (data, targetStep) => {
			const errors = [];

			const isDependantDataLoading = data.fetchPlanRequestState === 'loading';

			const hasAnySelections = data?.studyPlan?.planSelections && data?.studyPlan?.planSelections.length > 0;

			if (!isDependantDataLoading && !hasAnySelections && targetStep > 3) {
				alertToast(`At least one ${window.lexicon.course} must be selected to generate the plan`, 'error');
			}

			return [
				!!(isDependantDataLoading || hasAnySelections),
				{},
			];
		},
		// Study plan
		async () => [true, {}],
	];

	return (
		<WizardContextProvider<IStudyPlanWizardData>
			baseUrl="/plan/edit"
			stepLabels={stepLabels}
			initialData={initialData}
			validators={validators}
			onChangeAndSave={onChangeAndSave}
			mode={WizardMode.EDIT}
		>
			<StudyPlanWizard {...props} />
		</WizardContextProvider>
	);
};

export default StudyPlanWizardContext;
